import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { toast } from "sonner";
import { api, errText, mediaUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ImagePlus, Loader2, Film, Music2, Clapperboard, Calendar, FileText, Link2, Lock, X } from "lucide-react";
import { MentionInput } from "./MentionInput";
import { GifPicker } from "./GifPicker";

function useUpload() {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.url;
    } finally { setUploading(false); }
  };
  return { upload, uploading };
}

function FilePick({ accept, value, onPick, label, icon: Icon }) {
  const ref = useRef();
  const { upload, uploading } = useUpload();
  return (
    <div>
      <input ref={ref} type="file" accept={accept} className="hidden"
        onChange={async (e) => { const f = e.target.files?.[0]; if (f) { try { onPick(await upload(f)); } catch (er) { toast.error(errText(er)); } } }} />
      <button type="button" data-testid="file-pick-btn" onClick={() => ref.current?.click()}
        className="w-full rounded-xl surface-2 border border-dashed border-white/15 hover:border-eblue/50 transition-colors py-6 flex flex-col items-center gap-2 text-muted-foreground">
        {uploading ? <Loader2 className="w-6 h-6 animate-spin text-eblue" /> : <Icon className="w-6 h-6" />}
        <span className="text-xs">{uploading ? "Uploading…" : label}</span>
      </button>
      {value && accept.includes("image") && <img src={mediaUrl(value)} alt="" className="mt-2 rounded-lg max-h-40 w-full object-cover" />}
      {value && accept.includes("video") && <video src={mediaUrl(value)} className="mt-2 rounded-lg max-h-40 w-full object-cover" />}
      {value && accept.includes("audio") && <div className="mt-2 text-xs text-eblue">Audio attached ✓</div>}
    </div>
  );
}

const MAX_TV_SECONDS = 5 * 60 * 60;

// Multi-image picker for posts — uploads several photos that render as a
// swipeable slideshow in the feed.
function MultiImagePick({ images, setImages, max = 10 }) {
  const ref = useRef();
  const { upload, uploading } = useUpload();
  const add = async (files) => {
    const list = Array.from(files).slice(0, max - images.length);
    for (const f of list) {
      try { const url = await upload(f); setImages((prev) => [...prev, url]); } catch (e) { toast.error(errText(e)); }
    }
  };
  return (
    <div>
      <input ref={ref} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => { if (e.target.files?.length) add(e.target.files); e.target.value = ""; }} data-testid="post-images-input" />
      {images.length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-2" data-testid="post-images-preview">
          {images.map((u, i) => (
            <div key={i} className="relative shrink-0">
              <img src={mediaUrl(u)} alt="" className="w-24 h-24 rounded-lg object-cover" />
              <button type="button" onClick={() => setImages(images.filter((_, j) => j !== i))} data-testid={`post-image-remove-${i}`} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-black/80 border border-white/20 flex items-center justify-center"><X size={13} /></button>
              {i === 0 && <span className="absolute bottom-1 left-1 text-[9px] bg-black/70 px-1.5 py-0.5 rounded">Cover</span>}
            </div>
          ))}
        </div>
      )}
      {images.length < max && (
        <button type="button" data-testid="post-add-image-btn" disabled={uploading} onClick={() => ref.current?.click()}
          className="w-full rounded-xl surface-2 border border-dashed border-white/15 hover:border-eblue/50 transition-colors py-6 flex flex-col items-center gap-2 text-muted-foreground">
          {uploading ? <Loader2 className="w-6 h-6 animate-spin text-eblue" /> : <ImagePlus className="w-6 h-6" />}
          <span className="text-xs">{uploading ? "Uploading…" : images.length ? "Add more photos" : "Add photos (swipe-through slideshow)"}</span>
        </button>
      )}
    </div>
  );
}


function readVideoDuration(file) {
  return new Promise((resolve) => {
    try {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.onloadedmetadata = () => { URL.revokeObjectURL(el.src); resolve(el.duration || 0); };
      el.onerror = () => resolve(0);
      el.src = URL.createObjectURL(file);
    } catch { resolve(0); }
  });
}

// Generic chunked uploader — bypasses proxy/body limits for large media
// (long-form VStar TV videos up to 5h, reel videos and music audio of any size).
function ChunkedFilePick({ accept, purpose, label, icon: Icon, value, onPick, maxSeconds, maxBytes, readyLabel, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);

  const handle = async (file) => {
    if (!file) return;
    let dur = 0;
    // Read duration for display only — no length/size cap on uploads.
    if (accept.includes("video")) {
      try { dur = await readVideoDuration(file); } catch { dur = 0; }
    }
    if (maxBytes && file.size > maxBytes) { toast.error(`That file is over ${Math.round(maxBytes / 1048576)} MB`); return; }
    if (maxSeconds && dur > maxSeconds + 1) { toast.error(`Videos can be up to ${Math.round(maxSeconds / 60)} minutes`); return; }
    setBusy(true); setPct(0);
    try {
      const { data: init } = await api.post("/upload/chunk/init", { filename: file.name, content_type: file.type || "application/octet-stream", purpose });
      const chunkSize = init.chunk_size || 5 * 1024 * 1024;
      const total = Math.ceil(file.size / chunkSize) || 1;
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * chunkSize, (i + 1) * chunkSize);
        const fd = new FormData();
        fd.append("upload_id", init.upload_id);
        fd.append("index", String(i));
        fd.append("file", blob, `chunk-${i}`);
        await api.post("/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPct(Math.round(((i + 1) / total) * 100));
      }
      const { data: done } = await api.post("/upload/chunk/complete", { upload_id: init.upload_id });
      onPick(done.url, dur);
      toast.success("Upload complete");
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <input ref={ref} type="file" accept={accept} className="hidden"
        onChange={(e) => handle(e.target.files?.[0])} data-testid={`${testid}-input`} />
      <button type="button" data-testid={`${testid}-pick-btn`} disabled={busy} onClick={() => ref.current?.click()}
        className="w-full rounded-xl surface-2 border border-dashed border-white/15 hover:border-eblue/50 transition-colors py-6 flex flex-col items-center gap-2 text-muted-foreground">
        {busy ? <Loader2 className="w-6 h-6 animate-spin text-eblue" /> : <Icon className="w-6 h-6" />}
        <span className="text-xs">{busy ? `Uploading… ${pct}%` : label}</span>
      </button>
      {busy && <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-eblue transition-all" style={{ width: `${pct}%` }} /></div>}
      {value && !busy && <div className="mt-2 text-xs text-eblue" data-testid={`${testid}-ready`}>{readyLabel || "File attached ✓"}</div>}
    </div>
  );
}

export function CreateModal({ open, onClose, onCreated, defaultTab = "post" }) {
  const { user } = useAuth();
  const isPlus = user?.plan === "plus" || user?.role === "admin";
  const [tab, setTab] = useState(defaultTab);
  const [busy, setBusy] = useState(false);
  // post/story
  const [text, setText] = useState("");
  const [img, setImg] = useState(null);
  const [images, setImages] = useState([]);
  const [link, setLink] = useState("");
  const [gifOpen, setGifOpen] = useState(false);
  const [postVid, setPostVid] = useState(null);
  // reel
  const [reelVid, setReelVid] = useState(null);
  const [caption, setCaption] = useState("");
  // music
  const [audio, setAudio] = useState(null); const [cover, setCover] = useState(null);
  const [title, setTitle] = useState(""); const [genre, setGenre] = useState("Pop");
  // video
  const [vfile, setVfile] = useState(null); const [vthumb, setVthumb] = useState(null);
  const [vtitle, setVtitle] = useState(""); const [vcat, setVcat] = useState("Series");
  const [vduration, setVduration] = useState(0);
  // event
  const [ev, setEv] = useState({ title: "", datetime: "", world: "Second Life", venue: "", join_url: "" });

  const reset = () => { setPostVid(null); setText(""); setImg(null); setImages([]); setLink(""); setReelVid(null); setCaption(""); setAudio(null); setCover(null); setTitle(""); setVfile(null); setVthumb(null); setVtitle(""); setVduration(0); setEv({ title: "", datetime: "", world: "Second Life", venue: "", join_url: "" }); };

  const done = (msg) => { toast.success(msg); reset(); onCreated && onCreated(); onClose(); };

  const submit = async () => {
    setBusy(true);
    try {
      if (tab === "post") {
        const media = postVid ? [{ url: postVid.url, type: "video", duration: postVid.dur }] : images.map((u) => ({ url: u, type: "image" }));
        if (!text && media.length === 0 && !link) { toast.error("Add something to post"); setBusy(false); return; }
        await api.post("/posts", { type: postVid ? "video" : media.length ? "image" : link ? "link" : "text", text, media, link: link || null });
        done("Posted to VFeed");
      } else if (tab === "story") {
        if (!img) { toast.error("Pick a story image"); setBusy(false); return; }
        await api.post("/stories", { media_url: img, media_type: "image", text });
        done("Story added");
      } else if (tab === "reel") {
        if (!reelVid) { toast.error("Upload a video"); setBusy(false); return; }
        await api.post("/reels", { video_url: reelVid, caption, hashtags: (caption.match(/#\w+/g) || []).map(h => h.slice(1)) });
        done("Reel published");
      } else if (tab === "music") {
        if (!audio || !title) { toast.error("Add audio + title"); setBusy(false); return; }
        await api.post("/music/tracks", { title, audio_url: audio, cover_url: cover, genre });
        done("Track released");
      } else if (tab === "video") {
        if (!vfile || !vtitle) { toast.error("Add video + title"); setBusy(false); return; }
        await api.post("/tv/videos", { title: vtitle, video_url: vfile, thumbnail_url: vthumb, category: vcat, duration: vduration });
        done("Published to VStar TV");
      } else if (tab === "event") {
        if (!ev.title || !ev.datetime) { toast.error("Title + date required"); setBusy(false); return; }
        await api.post("/events", { ...ev, datetime: new Date(ev.datetime).toISOString() });
        done("Event created");
      }
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const Gate = () => (
    <div className="flex flex-col items-center py-8 text-center gap-2">
      <Lock className="w-7 h-7 text-gold" />
      <div className="font-display font-semibold">VirtuStars+ required</div>
      <p className="text-sm text-muted-foreground">Upgrade to upload music & long-form TV, and unlock creator tools.</p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="glass border-white/10 sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="create-modal">
        <DialogHeader><DialogTitle className="font-display">Create</DialogTitle><DialogDescription className="sr-only">Create a post, story, reel, music, video or event</DialogDescription></DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-6 bg-transparent gap-1 h-auto">
            {[["post", FileText], ["story", ImagePlus], ["reel", Film], ["music", Music2], ["video", Clapperboard], ["event", Calendar]].map(([k, Icon]) => (
              <TabsTrigger key={k} value={k} data-testid={`create-tab-${k}`}
                className="flex-col gap-1 py-2 data-[state=active]:bg-white/10 data-[state=active]:text-eblue rounded-lg capitalize text-[11px]">
                <Icon size={16} />{k}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="post" className="space-y-3 pt-4">
            <MentionInput placeholder="Share with the industry… use @ to mention" value={text} onChange={setText}
              className="w-full surface-2 border border-white/10 rounded-md min-h-[100px] p-3 text-sm outline-none" testid="post-text-input" />
            {!postVid && <MultiImagePick images={images} setImages={setImages} />}
            {images.length === 0 && (postVid
              ? <div className="relative mt-2" data-testid="post-video-preview"><video src={mediaUrl(postVid.url)} controls className="w-full max-h-56 rounded-xl bg-black" />
                  <button type="button" onClick={() => setPostVid(null)} data-testid="post-video-remove" className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/80 flex items-center justify-center"><X size={14} /></button></div>
              : <div className="mt-2"><ChunkedFilePick accept="video/*,.mp4,.mov,.m4v,.webm" purpose="post_video" maxSeconds={120} maxBytes={200 * 1024 * 1024} value={null} onPick={(url, dur) => setPostVid({ url, dur })} label="Or add a video (up to 2 min, 200 MB)" icon={Film} testid="post-video" /></div>)}
            <button type="button" onClick={() => setGifOpen(true)} data-testid="post-gif-btn"
              className="flex items-center gap-2 text-xs font-semibold text-eblue hover:text-eblue/80 transition-colors">
              <span className="px-1.5 py-0.5 rounded bg-eblue/15 border border-eblue/30 text-[10px] font-bold tracking-wide">GIF</span>
              Add a GIF
            </button>
            <div className="flex items-center gap-2 surface-2 border border-white/10 rounded-xl px-3">
              <Link2 size={16} className="text-muted-foreground" />
              <input placeholder="Attach a virtual-world link (optional)" value={link} onChange={(e) => setLink(e.target.value)}
                className="bg-transparent flex-1 py-2.5 text-sm outline-none" data-testid="post-link-input" />
            </div>
          </TabsContent>
          <TabsContent value="story" className="space-y-3 pt-4">
            <FilePick accept="image/*" value={img} onPick={setImg} label="Story image" icon={ImagePlus} />
            <Input placeholder="Caption (optional)" value={text} onChange={(e) => setText(e.target.value)} className="surface-2 border-white/10" />
          </TabsContent>
          <TabsContent value="reel" className="space-y-3 pt-4">
            <ChunkedFilePick accept="video/*,.mp4,.mov,.m4v,.webm,.avi,.mkv,.hevc" purpose="reel" value={reelVid} onPick={(url) => setReelVid(url)} label="Upload vertical video" readyLabel="Video attached ✓" icon={Film} testid="reel-video" />
            <Textarea placeholder="Caption with #hashtags" value={caption} onChange={(e) => setCaption(e.target.value)} className="surface-2 border-white/10" data-testid="reel-caption-input" />
          </TabsContent>
          <TabsContent value="music" className="space-y-3 pt-4">
            {!isPlus ? <Gate /> : <>
              <ChunkedFilePick accept="audio/*,.mp3,.wav,.m4a,.aac,.flac,.aif,.aiff,.ogg,.oga,.opus,.wma,.alac" purpose="music" value={audio} onPick={(url) => setAudio(url)} label="Upload audio" readyLabel="Audio attached ✓" icon={Music2} testid="music-audio" />
              <FilePick accept="image/*" value={cover} onPick={setCover} label="Cover artwork" icon={ImagePlus} />
              <Input placeholder="Song title" value={title} onChange={(e) => setTitle(e.target.value)} className="surface-2 border-white/10" data-testid="music-title-input" />
              <select value={genre} onChange={(e) => setGenre(e.target.value)} className="w-full surface-2 border border-white/10 rounded-xl px-3 py-2.5 text-sm">
                {["Hip-Hop", "R&B", "Pop", "Electronic", "Rock", "Alternative", "Virtual DJs", "Other"].map(g => <option key={g}>{g}</option>)}
              </select>
            </>}
          </TabsContent>
          <TabsContent value="video" className="space-y-3 pt-4">
            {!isPlus ? <Gate /> : <>
              <ChunkedFilePick accept="video/*,.mp4,.mov,.m4v,.webm,.avi,.mkv,.hevc" purpose="tv" value={vfile} onPick={(url, dur) => { setVfile(url); setVduration(dur || 0); }} label="Upload video (any length)" readyLabel={`Video attached ✓${vduration ? ` · ${Math.floor(vduration / 60)}m ${Math.floor(vduration % 60)}s` : ""}`} icon={Clapperboard} testid="tv-video" />
              <FilePick accept="image/*" value={vthumb} onPick={setVthumb} label="Thumbnail" icon={ImagePlus} />
              <Input placeholder="Title" value={vtitle} onChange={(e) => setVtitle(e.target.value)} className="surface-2 border-white/10" />
              <select value={vcat} onChange={(e) => setVcat(e.target.value)} className="w-full surface-2 border border-white/10 rounded-xl px-3 py-2.5 text-sm">
                {["Series", "Movies", "Music Videos", "Talk Shows", "Sports", "Gaming"].map(c => <option key={c}>{c}</option>)}
              </select>
            </>}
          </TabsContent>
          <TabsContent value="event" className="space-y-3 pt-4">
            <Input placeholder="Event title" value={ev.title} onChange={(e) => setEv({ ...ev, title: e.target.value })} className="surface-2 border-white/10" data-testid="event-title-input" />
            <Input type="datetime-local" value={ev.datetime} onChange={(e) => setEv({ ...ev, datetime: e.target.value })} className="surface-2 border-white/10" />
            <div className="grid grid-cols-2 gap-2">
              <select value={ev.world} onChange={(e) => setEv({ ...ev, world: e.target.value })} className="surface-2 border border-white/10 rounded-xl px-3 py-2.5 text-sm">
                {["Second Life", "IMVU", "FiveM", "VRChat", "Roblox", "Other"].map(w => <option key={w}>{w}</option>)}
              </select>
              <Input placeholder="Venue" value={ev.venue} onChange={(e) => setEv({ ...ev, venue: e.target.value })} className="surface-2 border-white/10" />
            </div>
            <Input placeholder="Teleport / join URL" value={ev.join_url} onChange={(e) => setEv({ ...ev, join_url: e.target.value })} className="surface-2 border-white/10" />
          </TabsContent>
        </Tabs>
        <Button className="btn-blue w-full h-11 rounded-xl mt-2" disabled={busy} onClick={submit} data-testid="create-submit-btn">
          {busy ? "Publishing…" : "Publish"}
        </Button>
        <GifPicker open={gifOpen} onClose={() => setGifOpen(false)} onSelect={(url) => setImages((prev) => prev.length < 10 ? [...prev, url] : prev)} />
      </DialogContent>
    </Dialog>
  );
}
